"use client";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Form, Select } from "antd";
import { useEffect, useMemo, useRef, useState } from "react";

import { SYSTEM_ATTRIBUTE_PROPERTY } from "~/core/constants/attributes";
import { useOrgStagesData } from "~/core/hooks/use-org-stages";

import { faChevronDown } from "@fortawesome/pro-regular-svg-icons";

import { SELECT_INPUT_FOCUS_DELAY } from "../../constants";
import { useGlobalNewOpportunityModalContext } from "../../context";
import { useGlobalCreateOpportunityStore } from "../../store";
import { DDRendererItem } from "../../types";
import DDClearIcon from "../DDClearIcon";

import { DefaultDDRenderer } from "./DefaultDDRenderer";
import StageLabel from "./StageLabel";

export default function StageSelect() {
  const { form, coreFieldsDivRef } = useGlobalNewOpportunityModalContext();
  const ref = useRef<any>(null);
  const [isOpen, setIsOpen] = useState(false);
  const {
    stages,
    setStages,
    setSelectedStage,
    selectedStage,
    isOpenGlobalCreateOpportunity,
    resetStageSelection,
  } = useGlobalCreateOpportunityStore();

  const selectedPipeline = useGlobalCreateOpportunityStore(
    state => state.selectedPipeline,
  );

  const { stages: fetchedStages, refetchStagesData } = useOrgStagesData({
    pipeline_id: selectedPipeline?.value,
  });

  useEffect(() => {
    if (selectedPipeline?.value) {
      refetchStagesData();
    }
  }, [selectedPipeline?.value, refetchStagesData]);

  const options = useMemo(() => {
    return fetchedStages
      ?.sort((a, b) => a.stage_order - b.stage_order)
      ?.map(item => {
        const id = item.stage_id;
        const label = item.label;
        const metadata = {
          label,
          color: item.color,
        };

        return {
          id,
          label: (
            <StageLabel
              stage={{
                id,
                label,
                metadata,
              }}
            />
          ),
          active: selectedStage?.id === id,
          searchableText: label,
          metadata,
        };
      });
  }, [fetchedStages, selectedStage?.id]);

  useEffect(() => {
    const filteredOptions = options?.filter(
      option => option.id !== selectedStage?.id,
    );
    setStages(
      selectedStage
        ? [{ ...selectedStage, active: true }, ...(filteredOptions || [])]
        : options || [],
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchedStages]);

  useEffect(() => {
    const currentStage = form?.getFieldValue(
      SYSTEM_ATTRIBUTE_PROPERTY.OPPORTUNITY_STAGE,
    );
    const doesCurrentStageExist = options?.some(
      item => item.id === currentStage,
    );

    if (!doesCurrentStageExist && isOpenGlobalCreateOpportunity) {
      form?.setFieldsValue({
        [SYSTEM_ATTRIBUTE_PROPERTY.OPPORTUNITY_STAGE]:
          fetchedStages?.[0]?.stage_id,
      });
      if (options) {
        setSelectedStage(
          options.find(
            item => item.id === fetchedStages?.[0]?.stage_id,
          ) as DDRendererItem,
        );
      }
    }
  }, [
    fetchedStages,
    form,
    isOpenGlobalCreateOpportunity,
    options,
    selectedPipeline?.value,
    setSelectedStage,
  ]);

  const handleDropdownClose = () => {
    setIsOpen(false);
    // Focus the select after closing
    setTimeout(() => {
      ref.current?.focus();
    }, SELECT_INPUT_FOCUS_DELAY);
  };

  return (
    <Form.Item name={SYSTEM_ATTRIBUTE_PROPERTY.OPPORTUNITY_STAGE} label="Stage">
      <Select
        allowClear={{
          clearIcon: <DDClearIcon />,
        }}
        onClear={() => {
          resetStageSelection();
          form?.resetFields([SYSTEM_ATTRIBUTE_PROPERTY.OPPORTUNITY_STAGE]);
        }}
        placeholder="Select stage"
        ref={ref}
        onDropdownVisibleChange={e => {
          setIsOpen(e);
        }}
        open={isOpen}
        labelRender={e => {
          const stage = selectedStage;
          if (!stage) return null;
          return <StageLabel stage={stage} />;
        }}
        onFocus={() => {
          setIsOpen(true);
        }}
        suffixIcon={
          selectedStage ? (
            <DDClearIcon asSuffix />
          ) : (
            <FontAwesomeIcon icon={faChevronDown} />
          )
        }
        value={form?.getFieldValue(SYSTEM_ATTRIBUTE_PROPERTY.OPPORTUNITY_STAGE)}
        dropdownRender={() => (
          <DefaultDDRenderer
            items={stages}
            onItemSelect={e => {
              form?.setFieldsValue({
                opportunity_stage: e.id as string,
              });
              setIsOpen(false);
              setSelectedStage(e);

              /**
               * @todo: Will be created more robust and reusable way
               */
              if (coreFieldsDivRef?.current) {
                // Scroll and focus
                coreFieldsDivRef.current.scrollIntoView({
                  behavior: "smooth",
                  block: "start",
                });

                // Focus the DatePicker after scrolling
                setTimeout(() => {
                  const datePickerInput =
                    coreFieldsDivRef.current?.querySelector(
                      ".ant-picker-input input",
                    );
                  if (datePickerInput) {
                    (datePickerInput as HTMLElement).focus();
                  }
                }, 100);
              }
            }}
            onCommandKeydown={e => {
              if (e.key === "Escape") {
                e.preventDefault();
                e.stopPropagation();
                handleDropdownClose();
              }
            }}
          />
        )}
      />
    </Form.Item>
  );
}
